<template>
  <v-container fluid class="mb-12">
    <v-row class="justify-center mb-5">
      <v-card-title>{{ $t('subOrder.synthesis') }}</v-card-title>
    </v-row>
    <v-container fluid class="mb-11 pa-0">
      <v-card v-if="Array.isArray(fullyPreparedSubOrders) && fullyPreparedSubOrders.length" outlined class="mb-6">
        <v-card-title>{{ $t('subOrder.completeOrders') }}</v-card-title>
        <v-data-table
          :headers="fullyPreparedSubOrdersHeaders"
          :items="fullyPreparedSubOrders"
          :hide-default-footer="true"
          :disable-pagination="true"
          mobile-breakpoint="0"
          show-select
          v-model="selected[0]"
          item-key="order_id"
          :singleSelect="false"
        >
          <template v-slot:header.data-table-select>
            <v-simple-checkbox
              :value="selected[0] && selected[0].length === selectableFullyPreparedSubOrders.length"
              :disabled="!selectableFullyPreparedSubOrders.length"
              @input="toggleSelectAll(0, selectableFullyPreparedSubOrders, $event)"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.order_id="{ item }">
            <p style="word-break: break-all" class="mb-0">{{ item.order_id }}</p>
          </template>
          <template v-slot:item.nbProducts="{ item }">
            {{ getSumScannedItems(item.items) }} / {{ getSumTotalItems(item.items) }}
          </template>
          <template v-slot:item.preparation_locker_id="{ item, index }">
            <span v-if="item.preparation_locker_id === 'no_available_locker'">{{ $t('subOrder.noAvailableLocker') }}</span>
            <span v-else-if="item.preparation_locker_id">{{ item.preparation_locker_id }}</span>
            <v-tooltip v-else v-model="showNoLockerSelectedTooltipFull[index]" top color="red">
              <template v-slot:activator="{ attrs }">
                <v-btn class="mb-1" small icon v-bind="attrs" @click="toggleLockerTooltipFull(index)">
                  <v-icon color="red">mdi-information-outline</v-icon>
                </v-btn>
              </template>
              {{ $t('subOrder.youMustPickALockerBeforeValidation') }}
            </v-tooltip>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn v-if="item.mustPickALocker" depressed icon :disabled="loadingBtn" @click="openLockerModal(item)">
              <v-icon>mdi-locker-multiple</v-icon>
            </v-btn>
          </template>
          <template v-slot:footer>
            <v-divider></v-divider>
            <v-toolbar flat dense>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="loadingBtn || !selected[0] || !selected[0].length"
                :loading="loadingBtn"
                text
                :x-small="$vuetify.breakpoint.width < 500"
                :small="$vuetify.breakpoint.width >= 500"
                @click="printDocuments(selected[0])"
              >
                {{ $t('subOrder.printDocuments') }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="loadingBtn || !selected[0] || !selected[0].length"
                :loading="loadingBtn"
                text
                :x-small="$vuetify.breakpoint.width < 500"
                :small="$vuetify.breakpoint.width >= 500"
                @click="validatePreparations(selected[0])"
              >
                {{ $t('subOrder.validatePreparations') }}
              </v-btn>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-spacer></v-spacer>
          </template>
        </v-data-table>
      </v-card>
      <v-card
        v-if="Array.isArray(partiallyPreparedSubOrders) && partiallyPreparedSubOrders.length"
        outlined
        class="mb-6"
      >
        <v-card-title>{{ $t('subOrder.incompleteOrders') }}</v-card-title>
        <v-data-table
          :headers="partiallyPreparedSubOrdersHeaders"
          :items="partiallyPreparedSubOrders"
          :hide-default-footer="true"
          :disable-pagination="true"
          mobile-breakpoint="0"
          show-select
          v-model="selected[1]"
          item-key="order_id"
          :singleSelect="false"
        >
          <template v-slot:header.data-table-select>
            <v-simple-checkbox
              :value="selected[1] && selected[1].length === selectablePartiallyPreparedSubOrders.length"
              :disabled="!selectablePartiallyPreparedSubOrders.length"
              @input="toggleSelectAll(1, selectablePartiallyPreparedSubOrders, $event)"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.order_id="{ item }">
            <p style="word-break: break-all" class="mb-0">{{ item.order_id }}</p>
          </template>
          <template v-slot:item.nbProducts="{ item, index }">
            {{ getSumScannedItems(item.items) }} / {{ getSumTotalItems(item.items) }}
            <v-tooltip v-if="!item.isSelectable && !lockerNotValid(item)" v-model="showNotPreparedTooltip[index]" top color="red">
              <template v-slot:activator="{ attrs }">
                <v-btn class="mb-1" small icon v-bind="attrs" @click="toggleTooltip(index)">
                  <v-icon color="red">mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span v-if="getOrderCompletion(item.items) === 0">
                {{ $t('subOrder.noProductPrepared') }}
              </span>
              <span v-else>
                <!-- because order preparation is not selectable and partially prepared so it means partial is not authorized -->
                {{ $t('subOrder.orderPreparationWillBeCancelled') }}
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.preparation_locker_id="{ item, index }">
            <span v-if="item.preparation_locker_id === 'no_available_locker'">{{ $t('subOrder.noAvailableLocker') }}</span>
            <span v-else-if="item.preparation_locker_id">{{ item.preparation_locker_id }}</span>
            <v-tooltip v-else v-model="showNotPreparedTooltip[index]" top color="red">
              <template v-slot:activator="{ attrs }">
                <v-btn class="mb-1" small icon v-bind="attrs" @click="toggleTooltip(index)">
                  <v-icon color="red">mdi-information-outline</v-icon>
                </v-btn>
              </template>
              {{ $t('subOrder.youMustPickALockerBeforeValidation') }}
            </v-tooltip>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn v-if="item.mustPickALocker" depressed icon :disabled="loadingBtn" @click="openLockerModal(item)">
              <v-icon>mdi-locker-multiple</v-icon>
            </v-btn>
          </template>
          <template v-slot:footer>
            <v-divider></v-divider>
            <v-toolbar flat dense>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="loadingBtn || !selected[1] || !selected[1].length"
                :loading="loadingBtn"
                text
                :x-small="$vuetify.breakpoint.width < 500"
                :small="$vuetify.breakpoint.width >= 500"
                @click="printDocuments(selected[1])"
              >
                {{ $t('subOrder.printDocuments') }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="loadingBtn || !selected[1] || !selected[1].length"
                :loading="loadingBtn"
                text
                :x-small="$vuetify.breakpoint.width < 500"
                :small="$vuetify.breakpoint.width >= 500"
                @click="validatePreparations(selected[1])"
              >
                {{ $t('subOrder.validatePreparations') }}
              </v-btn>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-spacer></v-spacer>
          </template>
        </v-data-table>
      </v-card>
      <v-card v-if="Array.isArray(validatedSubOrders) && validatedSubOrders.length" outlined class="mb-6">
        <v-card-title>{{ $t('subOrder.validatedOrders') }}</v-card-title>
        <v-data-table
          :headers="validatedPreparationHeaders"
          :items="validatedSubOrders"
          :hide-default-footer="true"
          :disable-pagination="true"
          mobile-breakpoint="0"
          show-select
          v-model="selected[2]"
          item-key="order_id"
          :singleSelect="false"
        >
          <template v-slot:item.order_id="{ item }">
            <p style="word-break: break-all" class="mb-0">{{ item.order_id }}</p>
          </template>
          <template v-slot:item.nbProducts="{ item }">
            {{ getSumScannedItems(item.items) }} / {{ getSumTotalItems(item.items) }}
          </template>
          <template v-slot:footer>
            <v-divider></v-divider>
            <v-toolbar flat dense>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="!selected[2] || !selected[2].length"
                :loading="loadingBtn"
                text
                :x-small="$vuetify.breakpoint.width < 500"
                :small="$vuetify.breakpoint.width >= 500"
                @click="printDocuments(selected[2])"
              >
                {{ $t('subOrder.printDocuments') }}
              </v-btn>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-spacer></v-spacer>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
    <locker-selection-dialog
      v-model="lockerSelectionModal"
      :lockerId="targetedSubOrder.preparation_locker_id"
      @close="closeLockerSelectionModal"
      @validate="validateLocker"
    ></locker-selection-dialog>
    <information-dialog
      v-model="notGeneratedDocumentsModal"
      :title="$t('subOrder.notGeneratedDocumentsTitle')"
      :introductionSentence="$t('subOrder.notGeneratedDocumentsMessage')"
    >
      <template v-slot:content>
        <v-container fluid class="mb-8">
          <v-list-item
            dense
            v-for="notGeneratedDocument of notGeneratedDocuments"
            v-bind:key="notGeneratedDocument._id"
          >
            <v-list-item-icon class="mr-1">
              <v-icon>mdi-square-medium</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-card-text class="py-0">
                {{ notGeneratedDocument.order_id }}: {{ notGeneratedDocument.error_message }}
              </v-card-text>
            </v-list-item-content>
          </v-list-item>
        </v-container>
      </template>
    </information-dialog>
  </v-container>
</template>
<script>
  import { EventBus } from '@/event-bus'
  import lockerSelectionDialog from '@/components/dialogs/lockerSelectionDialog'
  import informationDialog from '@/components/dialogs/InformationDialog.vue'

  export default {
    name: 'pickPackShipStep3',
    components: {
      informationDialog,
      lockerSelectionDialog,
    },
    props: {
      subOrders: {
        type: Array,
        required: true,
      },
      typesRequiringLocker: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        selected: [],
        loadingBtn: false,
        validatedSubOrders: [],
        fullyPreparedSubOrders: [],
        partiallyPreparedSubOrders: [],
        showNotPreparedTooltip: [],
        showNoLockerSelectedTooltipFull: [],
        lockerSelectionModal: false,
        targetedSubOrder: {},
        pollJob: null,
        notGeneratedDocumentsModal: false,
        notGeneratedDocuments: [],
      }
    },
    created() {
      this.fillFullyPreparedSubOrders()
      this.fillPartiallyPreparedSubOrders()
      this.fillValidatedSubOrders()
      const remainingPreparations = [...this.fullyPreparedSubOrders, ...this.partiallyPreparedSubOrders]
      if (remainingPreparations.length === 0 && this.validatedSubOrders.length === 0) {
        this.$emit('allPreparationsRemoved')
      } else {
        this.$emit('validatePreparations', remainingPreparations)
      }
    },
    destroyed() {
      clearInterval(this.pollJob)
    },
    computed: {
      fullyPreparedSubOrdersHeaders() {
        return this.generateSubOrdersHeaders(this.fullyPreparedSubOrders)
      },
      partiallyPreparedSubOrdersHeaders() {
        return this.generateSubOrdersHeaders(this.partiallyPreparedSubOrders)
      },
      selectableFullyPreparedSubOrders() {
        return this.fullyPreparedSubOrders.filter((subOrder) => subOrder.isSelectable !== false)
      },
      selectablePartiallyPreparedSubOrders() {
        return this.partiallyPreparedSubOrders.filter((subOrder) => subOrder.isSelectable !== false)
      },
      validatedPreparationHeaders: function () {
        return [
          { text: this.$t('subOrder.binId'), value: 'binId', sortable: false, width: '15%', align: 'center' },
          {
            text: this.$t('subOrder.preparationId'),
            value: 'order_id',
            sortable: false,
            width: '50%',
            align: 'center',
          },
          { text: this.$t('subOrder.nbProducts'), value: 'nbProducts', sortable: false, width: '25%', align: 'center' },
        ]
      },
      totalNbProduct: function () {
        let totalNbProduct = 0
        for (let subOrder of this.subOrders) {
          totalNbProduct += subOrder.items.reduce((a, b) => a + b.product_quantity, 0)
        }
        return totalNbProduct
      },
    },
    methods: {
      toggleSelectAll(tableIndex, selectableItems, checked) {
        this.$set(this.selected, tableIndex, checked ? [...selectableItems] : [])
      },
      getSumScannedItems(items) {
        let totalNbProduct = 0
        totalNbProduct += items.reduce((a, b) => a + b.nb_scanned, 0)
        return totalNbProduct
      },
      getSumTotalItems(items) {
        let totalNbProduct = 0
        totalNbProduct += items.reduce((a, b) => a + b.product_quantity, 0)
        return totalNbProduct
      },
      printDocuments(subOrders) {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('subOrder.documentsGenerationInProgress'),
          color: 'success',
        })
        this.loadingBtn = true

        let path = '/v1/int/sub-orders/documents'
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: subOrders,
        }
        let vm = this
        this.$Amplify.API.post('orders', path, myInit)
          .then(function (res) {
            if (res.job_id) {
              vm.pollJob = setInterval(function () {
                vm.$Amplify.API.get('orders', '/v1/int/jobs/' + res.job_id, {
                  headers: { Authorization: 'Bearer ' + vm.$auth.accessToken },
                })
                  .then(function (res) {
                    if (Array.isArray(res.unitary_generated_files) && res.unitary_generated_files.length) {
                      vm.notGeneratedDocuments = vm.getNotGeneratedDocuments(res)
                      if (Array.isArray(vm.notGeneratedDocuments) && vm.notGeneratedDocuments.length) {
                        vm.notGeneratedDocumentsModal = true
                      }
                    }
                    let url = res.file_archive_url
                    if (url) {
                      //we create an <a> element, and we simulate a click on it to trigger the downloading
                      const link = document.createElement('a')
                      link.href = url
                      document.body.appendChild(link)
                      link.click()
                      clearInterval(vm.pollJob)
                      vm.loadingBtn = false
                    } else if (res.status === 'error') {
                      console.log(res.error_message)
                      EventBus.$emit('displaySnackbar', {
                        display: true,
                        text: vm.$t('subOrder.jobLoadingErrorMessage'),
                        color: 'error',
                      })
                      clearInterval(vm.pollJob)
                      vm.loadingBtn = false
                    }
                  })
                  .catch(function (e) {
                    console.log(e)
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: vm.$t('subOrder.jobLoadingErrorMessage'),
                      color: 'error',
                    })
                    clearInterval(vm.pollJob)
                    vm.loadingBtn = false
                  })
              }, 1000)
            } else {
              vm.loadingBtn = false
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('subOrder.jobLoadingErrorMessage'),
                color: 'error',
              })
            }
          })
          .catch(function (e) {
            console.log(e)
            vm.loadingBtn = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.jobCreationErrorMessage'),
              color: 'error',
            })
          })
      },
      async validatePreparations(subOrders) {
        if (this.checkDateLimitBeforeTimeoutReached()) {
          return
        }

        this.loadingBtn = true
        EventBus.$emit('displayGlobalOverlay', {
          display: true,
          message: this.$t('subOrder.processingPreparationOrders'),
        })

        let patchData = subOrders.map((subOrder) => {
          return {
            order_id: subOrder.order_id,
            current_status: ['click_and_reserve', 'click_and_collect'].includes(subOrder.type)
              ? 'to_pickup'
              : 'waiting_carrier',
            items: subOrder.items.map((item) => {
              return {
                product_id: item.product_id,
                item_id: item.item_id,
                current_status: this.getItemPreparationStatus(item),
                validated_quantity: item.nb_scanned,
                selected_product_positions: item.selected_product_positions,
              }
            }),
            preparation_locker_id: subOrder.preparation_locker_id,
          }
        })

        const patchSubOrderResponse = await this.patchSubOrders(patchData)
        if (patchSubOrderResponse === false) {
          this.$emit('updateError')
        } else {
          const notUpdatedSubOrders = patchSubOrderResponse?.notUpdatedSubOrders
            ? Object.entries(patchSubOrderResponse.notUpdatedSubOrders)
            : []
          if (Object.keys(patchSubOrderResponse.notUpdatedSubOrders).length) {
            this.$emit('notUpdatedSubOrders', notUpdatedSubOrders)
          }
          const notUpdatedSubOrdersIds = Object.keys(patchSubOrderResponse.notUpdatedSubOrders)

          // we get all the newly validated sub orders to display them in another table with the other validated sub orders
          const newlyValidatedSubOrdersIds = patchData
            .filter((patchedSubOrder) => !notUpdatedSubOrdersIds.includes(patchedSubOrder.order_id))
            .map((patchedSubOrder) => patchedSubOrder.order_id)
          const newlyValidatedSubOrders = this.subOrders.filter((subOrder) =>
            newlyValidatedSubOrdersIds.includes(subOrder.order_id)
          )
          this.validatedSubOrders = [...this.validatedSubOrders, ...newlyValidatedSubOrders]

          // we delete the validated sub orders from the remaining sub orders to validate
          this.fullyPreparedSubOrders = this.fullyPreparedSubOrders.filter(
            (subOrder) =>
              !newlyValidatedSubOrdersIds.includes(subOrder.order_id) &&
              !notUpdatedSubOrdersIds.includes(subOrder.order_id)
          )
          this.partiallyPreparedSubOrders = this.partiallyPreparedSubOrders.filter(
            (subOrder) =>
              !newlyValidatedSubOrdersIds.includes(subOrder.order_id) &&
              !notUpdatedSubOrdersIds.includes(subOrder.order_id)
          )
        }
        this.selected = []
        const remainingPreparations = [...this.fullyPreparedSubOrders, ...this.partiallyPreparedSubOrders]
        if (remainingPreparations.length === 0 && this.validatedSubOrders.length === 0) {
          this.$emit('allPreparationsRemoved')
        } else {
          this.$emit('validatePreparations', remainingPreparations)
        }
        EventBus.$emit('displayGlobalOverlay', { display: false })
        this.loadingBtn = false
      },
      async patchSubOrders(patchData) {
        let path = '/v1/int/sub-orders/bulk'
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: patchData,
        }
        try {
          const patchSubOrdersResponse = await this.$Amplify.API.patch('sub-orders', path, myInit)
          EventBus.$emit('reLoadCounter')
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.orderUpdated'),
            color: 'success',
          })
          return patchSubOrdersResponse
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.anErrorOccurredValidatingPreparationsMessage'),
            color: 'error',
          })
          return false
        }
      },
      checkDateLimitBeforeTimeoutReached() {
        let anyDateLimitBeforeTimeoutReached = this.subOrders.filter((subOrder) => subOrder.remainingTime <= 0)
        if (anyDateLimitBeforeTimeoutReached.length) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.dateLimiteReached'),
            color: 'error',
          })
        }
        return anyDateLimitBeforeTimeoutReached.length
      },
      getItemPreparationStatus(item) {
        const completion = item.nb_scanned / item.product_quantity
        let currentStatus
        if (completion === 0) {
          currentStatus = 'cancelled'
        } else if (completion === 1) {
          currentStatus = 'validated'
        } else {
          currentStatus = 'partially_validated'
        }
        return currentStatus
      },
      toggleTooltip(index) {
        this.$set(this.showNotPreparedTooltip, index, !this.showNotPreparedTooltip[index])
        setTimeout(() => {
          this.$set(this.showNotPreparedTooltip, index, false)
        }, 2000)
      },
      toggleLockerTooltipFull(index) {
        this.$set(this.showNoLockerSelectedTooltipFull, index, !this.showNoLockerSelectedTooltipFull[index])
        setTimeout(() => {
          this.$set(this.showNoLockerSelectedTooltipFull, index, false)
        }, 2000)
      },
      getOrderCompletion(items) {
        return this.getSumScannedItems(items) / this.getSumTotalItems(items)
      },
      fillPartiallyPreparedSubOrders() {
        for (let computedSubOrder of this.subOrders) {
          const preparationCompletion = this.getOrderCompletion(computedSubOrder.items)
          if (
            preparationCompletion < 1 &&
            !['waiting_carrier', 'to_pickup'].includes(computedSubOrder.current_status)
          ) {
            // if the completion is not valid (not prepared or partial but partial is not authorized
            if (preparationCompletion === 0 || !computedSubOrder.applied_rule?.authorize_balance) {
              computedSubOrder.isSelectable = false
            } else {
              // if the completion is valid we add further information about the preparation such as if a locker is required
              computedSubOrder.mustPickALocker = this.typesRequiringLocker.includes(computedSubOrder.type)
              if (computedSubOrder.mustPickALocker && !computedSubOrder.preparation_locker_id) {
                computedSubOrder.isSelectable = false
              } else {
                computedSubOrder.isSelectable = true
              }
            }
            this.partiallyPreparedSubOrders.push(computedSubOrder)
          }
          this.partiallyPreparedSubOrders.sort((subOrderA, subOrderB) => {
            let subOrderAIsSelectableNumericValue = subOrderA.isSelectable ? 1 : 0
            let subOrderBIsSelectableNumericValue = subOrderB.isSelectable ? 1 : 0
            return subOrderBIsSelectableNumericValue - subOrderAIsSelectableNumericValue
          })
        }
      },
      fillFullyPreparedSubOrders() {
        for (let computedSubOrder of this.subOrders) {
          const preparationCompletion = this.getOrderCompletion(computedSubOrder.items)
          if (
            preparationCompletion === 1 &&
            !['waiting_carrier', 'to_pickup'].includes(computedSubOrder.current_status)
          ) {
            computedSubOrder.mustPickALocker = this.typesRequiringLocker.includes(computedSubOrder.type)
            if (computedSubOrder.mustPickALocker && !computedSubOrder.preparation_locker_id) {
              computedSubOrder.isSelectable = false
            } else {
              computedSubOrder.isSelectable = true
            }

            this.fullyPreparedSubOrders.push(computedSubOrder)
          }
        }
      },
      fillValidatedSubOrders() {
        for (let computedSubOrder of this.subOrders) {
          if (['waiting_carrier', 'to_pickup'].includes(computedSubOrder.current_status)) {
            computedSubOrder.mustPickALocker = this.typesRequiringLocker.includes(computedSubOrder.type)
            this.validatedSubOrders.push(computedSubOrder)
          }
        }
      },
      generateSubOrdersHeaders(subOrders) {
        const hasLocker = subOrders.some((subOrder) => subOrder.mustPickALocker === true)
        const headers = [
          { text: this.$t('subOrder.binId'), value: 'binId', sortable: false, width: hasLocker ? '10%' : '15%', align: 'center' },
          {
            text: this.$t('subOrder.preparationId'),
            value: 'order_id',
            sortable: false,
            width: hasLocker ? '35%' : '50%',
            align: 'center',
          },
          { text: this.$t('subOrder.nbProducts'), value: 'nbProducts', sortable: false, width: hasLocker ? '15%' : '25%', align: 'center' },
        ]
        // we check if any of the suborders need a locker picking, if so we add the header
        if (hasLocker) {
          headers.push({ text: this.$t('subOrder.locker'), value: 'preparation_locker_id', sortable: false, width: '15%', align: 'center' })
          headers.push({ text: '', value: 'action', sortable: false, width: '5%', align: 'center' })
        }
        return headers
      },
      validateLocker(lockerId) {
        this.$set(this.targetedSubOrder, 'preparation_locker_id', lockerId)
        this.$set(this.targetedSubOrder, 'isSelectable', this.targetedSubOrder.preparation_locker_id !== undefined)
        this.$emit('lockerSelected', { order_id: this.targetedSubOrder.order_id, preparation_locker_id: lockerId })
        this.targetedSubOrder = {}
        this.lockerSelectionModal = false
      },
      openLockerModal(subOrder) {
        this.targetedSubOrder = subOrder
        this.lockerSelectionModal = true
      },
      closeLockerSelectionModal() {
        this.lockerSelectionModal = false
        this.targetedSubOrder = {}
      },
      lockerNotValid(subOrder) {
        // memo: subOrder.mustPickALocker is undefined on preparation if partial is not authorized and completion < 1
        if (subOrder.mustPickALocker) {
          return subOrder.preparation_locker_id === undefined
        }
        return false
      },
      getNotGeneratedDocuments(res) {
        return res.unitary_generated_files
          .filter((notGeneratedDocument) => ![200, '200'].includes(notGeneratedDocument.status_code))
          .map((notGeneratedDocument) => {
            return {
              order_id: notGeneratedDocument.order_id,
              error_message: `${notGeneratedDocument.status_code} - ${notGeneratedDocument.error_message}`,
            }
          })
      },
    },
  }
</script>
<style scoped>
  @media (max-width: 600px) {
    ::v-deep .v-data-table td,
    ::v-deep .v-data-table th {
      padding: 0 4px !important;
    }
    ::v-deep .v-data-table .v-data-table-header th.column:first-child,
    ::v-deep .v-data-table td:first-child {
      padding: 0 2px !important;
    }
    ::v-deep .v-toolbar__content {
      padding: 0;
    }
  }
  ::v-deep .v-simple-checkbox--disabled {
    opacity: 0.38;
  }
</style>
